import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PropertyCard from '../components/PropertyCard';
import './PropertyDetails.css';

// --- Icon SVG Components ---
const MapPinIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path><circle cx="12" cy="10" r="3"></circle></svg>;
const BedIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 4v16M2 8h18a2 2 0 0 1 2 2v10M2 17h20M6 8v9"></path></svg>;
const BathIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6v4M5 9.12195206v-3.12195206c0-1.1045695.8954305-2 2-2h10c1.1045695 0 2 .8954305 2 2v3.12195206M3 14h18M5 14v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6M12 18v-4"></path></svg>;
const AreaIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4zM4 10h16M10 4v16"></path></svg>;
const CalendarIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>;
const PhoneIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.08 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.63 2.62a2 2 0 0 1-.45 2.11L8 9.91a16 16 0 0 0 6.09 6.09l1.46-1.27a2 2 0 0 1 2.11-.45c.84.3 1.72.51 2.62.63A2 2 0 0 1 22 16.92z"></path></svg>;
const WifiIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"></path></svg>;
const DefaultIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>;

const PropertyDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [relatedProperties, setRelatedProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showContactOptions, setShowContactOptions] = useState(false);

    useEffect(() => {
        // App deep link redirect map
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (isMobile) {
            window.location.href = `rentahome://therentcompany.com/properties/${id}`;
        }
    }, [id]);

    useEffect(() => {
        window.scrollTo(0, 0);

        const loadData = async () => {
            setLoading(true);
            try {
                const { data, error } = await supabase
                    .from('property')
                    .select('*, users(full_name, email), areas(*)')
                    .eq('id', id)
                    .single();

                if (error) throw error;

                const mappedProperty = {
                    ...data,
                    price: data.expected_rent,
                    images: data.media_url || [],
                    bedrooms: data.bhk,
                    bathrooms: data.bathrooms || 1,
                    area_sqft: data.sqft,
                    location: data.areas ? `${data.areas.name}, ${data.areas.city}` : 'Location Available Soon',
                    street_address: data.address_line || '',
                    owner_name: data.users?.full_name || 'Property Manager',
                    listing_ref: `STRL-${data.id ? data.id.toString().substring(0, 3) : '982'}`,
                    area_name: data.areas?.name || '',
                    city_name: data.areas?.city || ''
                };

                setProperty(mappedProperty);

                const locality = data.areas?.name;
                const city = data.areas?.city;

                let relatedQuery = supabase
                    .from('property')
                    .select('*, areas(city, name)')
                    .eq('status', 'active')
                    .neq('id', id)
                    .limit(8);

                if (locality) {
                    relatedQuery = relatedQuery.eq('areas.name', locality);
                } else if (city) {
                    relatedQuery = relatedQuery.eq('areas.city', city);
                }

                const { data: relatedData, error: relatedError } = await relatedQuery;

                if (!relatedError && relatedData) {
                    const mappedRelated = relatedData.map((p) => ({
                        ...p,
                        price: p.expected_rent,
                        images: p.media_url || [],
                        bedrooms: p.bhk,
                        bathrooms: p.bathrooms,
                        area_sqft: p.sqft,
                        location: p.areas
                            ? `${p.areas.name}, ${p.areas.city}`
                            : 'Unknown Location',
                    }));

                    setRelatedProperties(mappedRelated);
                } else {
                    setRelatedProperties([]);
                }
            } catch (err) {
                console.error("Error loading property:", err);
                setError("Failed to load property details.");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id]);

    // Format helpers
    const formatDate = (dateString) => {
        if (!dateString) return 'Immediately';
        const d = new Date(dateString);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    if (loading) {
        return (
            <div className="pd-page-container">
                <Navbar />
                <div className="pd-main-wrapper" style={{ display: 'flex', gap: '2rem' }}>
                    <div className="pd-skeleton-hero">
                        <div className="pd-skeleton-image skeleton-loader"></div>
                        <div className="pd-skeleton-info">
                            <div className="pd-skeleton-title skeleton-loader"></div>
                            <div className="pd-skeleton-text skeleton-loader" style={{ width: '60%' }}></div>
                            <div className="pd-skeleton-rect skeleton-loader"></div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    if (error) return <div className="pd-page-container"><Navbar /><div style={{ padding: '4rem', textAlign: 'center' }}>{error}</div></div>;
    if (!property) return <div className="pd-page-container"><Navbar /><div style={{ padding: '4rem', textAlign: 'center' }}>Property not found.</div></div>;

    const images = property.images;
    const isRented = property.status?.toLowerCase() === 'rented';

    return (
        <div className="pd-page-container">
            <Navbar />
            <SEO
                title={`${property.bhk} BHK ${property.property_type} in ${property.area_name || property.city_name}`}
                description={`Rent this ${property.area_sqft} sqft property for ₹${property.price}/month.`}
                image={property.images?.[0]}
            />

            <main className="pd-main-wrapper">

                {/* Asymmetric Image Grid */}
                {images && images.length > 0 ? (
                    <div className="pd-image-grid-container">
                        <img src={images[0]} alt="Main View" className="pd-img-main" />

                        {/* Up to 4 small images on the right */}
                        {images.slice(1, 5).map((img, idx) => {
                            const isLastVisible = idx === 3;
                            const hasExtra = images.length > 5;
                            return (
                                <div
                                    key={idx}
                                    className={`pd-img-wrapper ${isLastVisible && hasExtra ? 'has-overlay' : ''}`}
                                    data-overlay={`+${images.length - 5} Photos`}
                                >
                                    <img src={img} alt={`View ${idx + 2}`} className="pd-img-sub" />
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="pd-placeholder-image">No Images Available</div>
                )}

                {/* Content Layout */}
                <div className="pd-content-layout">

                    {/* Left Column: Main Property Details */}
                    <div className="pd-main-column">
                        <div className="pd-hero-title-area">
                            {isRented && (
                                <div className="pd-status-badge">Rented</div>
                            )}
                            <h1 className="pd-title">{property.title || `${property.bedrooms} BHK ${property.property_type || 'Apartment'}`}</h1>
                            <div className="pd-location">
                                <MapPinIcon /> {property.street_address}, {property.location}
                            </div>
                        </div>

                        <div className="pd-specs-row">
                            <div className="pd-spec-chip"><BedIcon /> {property.bedrooms} Beds</div>
                            <div className="pd-spec-chip"><BathIcon /> {property.bathrooms} Baths</div>
                            <div className="pd-spec-chip"><AreaIcon /> {property.area_sqft || '--'} sqft</div>
                            <div className="pd-spec-chip">
                                <span className="icon-gold"><CalendarIcon /></span>
                                Available {formatDate(property.availability_date)}
                            </div>
                        </div>

                        {property.description && property.description.trim() && (
                            <div className="pd-section">
                                <h2>About this property</h2>
                                <div className="pd-description-text">
                                    <p>{property.description}</p>
                                </div>
                            </div>
                        )}

                        {property.amenities && property.amenities.length > 0 && (
                            <div className="pd-section">
                                <h2>Property Amenities</h2>
                                <div className="pd-amenities-grid">
                                    {property.amenities.map((amenity, i) => (
                                        <div key={i} className="pd-amenity-card">
                                            {amenity.toLowerCase().includes('wifi') ? <WifiIcon /> : <DefaultIcon />}
                                            {amenity}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Sticky Sidebar */}
                    <div className="pd-sidebar-wrapper">
                        <div className="pd-sidebar">

                            <div className="pd-price-header">
                                <div className={`pd-price ${isRented ? 'is-rented' : ''}`}>₹{property.price ? property.price.toLocaleString() : '0'}<span className="pd-price-mo">/mo</span></div>
                                <div className="pd-utilities-badge">Deposit: ₹{property.security_deposit || '0'}</div>
                            </div>

                            <div className="pd-action-card">
                                <h3>{isRented ? 'This property is rented' : 'Ready to move in?'}</h3>
                                <p>
                                    {isRented
                                        ? 'You can still contact us to check similar properties in the same area.'
                                        : 'Schedule a private tour with our property manager today.'}
                                </p>

                                <button
                                    className="pd-btn-primary"
                                    type="button"
                                    onClick={() => setShowContactOptions((value) => !value)}
                                >
                                    <PhoneIcon /> Contact
                                </button>

                                {showContactOptions && (
                                    <div className="pd-contact-popup">
                                        <a href="tel:+919620981400" className="pd-contact-option">
                                            +91 96209 81400
                                        </a>
                                        <a href="tel:+916366699999" className="pd-contact-option">
                                            +91 63666 99999
                                        </a>
                                    </div>
                                )}
                            </div>

                            <div className="pd-meta-footer">
                                Listed {formatDate(property.created_at)} &bull; Ref: {property.listing_ref}
                            </div>
                        </div>
                    </div>

                </div>

                {relatedProperties.length > 0 && (
                    <section className="pd-related-section">
                        <div className="pd-related-header">
                            <h2>More properties from this area</h2>
                            <Link
                                to={`/search?localities=${encodeURIComponent(property.area_name || property.city_name || '')}`}
                                className="pd-related-link"
                            >
                                View all
                            </Link>
                        </div>

                        <div className="pd-related-carousel">
                            {relatedProperties.map((item) => (
                                <div key={item.id} className="pd-related-card">
                                    <PropertyCard property={item} />
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </main>

            <Footer />
        </div>
    );
};

export default PropertyDetails;
